import React, { useEffect, useState } from "react";
import { api, formatIDR, formatDate } from "@/lib/api";
import { toast } from "sonner";
import {
  RefreshCw, CheckCircle2, AlertTriangle, HelpCircle, Download,
  RotateCcw, KeyRound, Wrench, Clock3, CheckCheck, CircleX, Eye,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const STATE_META = {
  match: { label: "MATCH", cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  need_action: { label: "NEED ACTION", cls: "bg-amber-100 text-amber-800", icon: AlertTriangle },
  unknown: { label: "UNKNOWN", cls: "bg-zinc-200 text-zinc-700", icon: HelpCircle },
  mismatch: { label: "MISMATCH", cls: "bg-rose-100 text-rose-800", icon: CircleX },
};

const safeDetail = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && typeof detail.message === "string") return detail.message;
  return fallback;
};

export default function Reconciliation() {
  const { user, hasFeature, hasPerm } = useAuth();
  const canRefund = ["admin", "supervisor"].includes(user?.role) && hasFeature("refunds") && hasPerm("sales.refund");
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState("");
  const [provider, setProvider] = useState("");
  const [start, setStart] = useState(new Date().toISOString().slice(0,10));
  const [end, setEnd] = useState(new Date().toISOString().slice(0,10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [busyRow, setBusyRow] = useState("");
  const [showOnly, setShowOnly] = useState("all");

  const [logTarget, setLogTarget] = useState(null);
  const [logs, setLogs] = useState([]);

  const [refundTarget, setRefundTarget] = useState(null);
  const [refundPin, setRefundPin] = useState("");
  const [refundReason, setRefundReason] = useState("Pembayaran terlambat setelah kasir menutup QRIS");
  const [refunding, setRefunding] = useState(false);

  const [correctionTarget, setCorrectionTarget] = useState(null);
  const [correctionPin, setCorrectionPin] = useState("");
  const [correctionReason, setCorrectionReason] = useState("Koreksi rekonsiliasi: Internal PAID tetapi Yokke belum sukses");
  const [correcting, setCorrecting] = useState(false);

  const [expireTarget, setExpireTarget] = useState(null);
  const [expirePin, setExpirePin] = useState("");
  const [expireReason, setExpireReason] = useState("QRIS melewati masa berlaku 5 menit");
  const [expiring, setExpiring] = useState(false);

  const [recoveryTarget, setRecoveryTarget] = useState(null);
  const [recoveryPin, setRecoveryPin] = useState("");
  const [recoveryReason, setRecoveryReason] = useState("Late payment recovery: provider sukses setelah Internal EXPIRED");
  const [recovering, setRecovering] = useState(false);

  useEffect(() => { api.get("/outlets").then(r => setOutlets(r.data || [])); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const params = () => ({
    outlet_id: outletId || undefined,
    provider: provider || undefined,
    start: start ? `${start}T00:00:00` : undefined,
    end: end ? `${end}T23:59:59` : undefined,
  });

  const load = async (refresh=false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const r = await api.get("/reports/reconciliation", {
        params: { ...params(), refresh, refresh_scope: "mismatch" },
      });
      setData(r.data);
      if (refresh) {
        const ok = r.data?.summary?.refreshed || 0;
        const fail = r.data?.summary?.refresh_failed || 0;
        if (fail) toast.warning(`${ok} berhasil, ${fail} inquiry provider gagal`);
        else toast.success(`${ok} transaksi di-refresh dari provider`);
      }
    } catch(e) { toast.error(safeDetail(e, "Gagal memuat rekonsiliasi")); }
    finally { setLoading(false); setRefreshing(false); }
  };

  const inquiryRow = async (row) => {
    setBusyRow(row.sale_id);
    try {
      const r = await api.get("/reports/reconciliation", {
        params: { sale_id: row.sale_id, refresh: true, refresh_scope: "all" },
      });
      if ((r.data?.summary?.refresh_failed || 0) > 0) toast.error(`Inquiry ${row.provider_label} gagal`);
      else toast.success(`Status ${row.invoice_no} berhasil di-refresh`);
      await load(false);
    } catch(e) { toast.error(safeDetail(e, "Inquiry transaksi gagal")); }
    finally { setBusyRow(""); }
  };

  const finalizePaid = async (row) => {
    setBusyRow(row.sale_id);
    try {
      await api.post(`/reports/reconciliation/${row.sale_id}/finalize-paid`);
      toast.success(`${row.invoice_no} berhasil difinalisasi menjadi PAID`);
      await load(false);
    } catch(e) { toast.error(safeDetail(e, "Finalisasi PAID gagal")); }
    finally { setBusyRow(""); }
  };

  const submitExpire = async () => {
    if (!expireTarget) return;
    if (!expirePin || expirePin.length < 4) return toast.error("PIN supervisor minimal 4 digit");
    setExpiring(true);
    try {
      await api.post(`/reports/reconciliation/${expireTarget.sale_id}/close-expired`, {
        supervisor_pin: expirePin,
        reason: expireReason,
      });
      toast.success(`${expireTarget.invoice_no} ditutup sebagai EXPIRED`);
      setExpireTarget(null);
      await load(false);
    } catch(e) { toast.error(safeDetail(e, "Closing EXPIRED gagal")); }
    finally { setExpiring(false); }
  };

  const submitRecovery = async () => {
    if (!recoveryTarget) return;
    if (!recoveryPin || recoveryPin.length < 4) return toast.error("PIN supervisor minimal 4 digit");
    setRecovering(true);
    try {
      await api.post(`/reports/reconciliation/${recoveryTarget.sale_id}/recover-expired-paid`, {
        supervisor_pin: recoveryPin,
        reason: recoveryReason,
      });
      toast.success(`${recoveryTarget.invoice_no} berhasil dipulihkan menjadi PAID`);
      setRecoveryTarget(null);
      await load(false);
    } catch (e) {
      toast.error(safeDetail(e, "Recovery late payment gagal"));
    } finally {
      setRecovering(false);
    }
  };

  const submitCorrection = async () => {
    if (!correctionTarget) return;
    if (!correctionPin || correctionPin.length < 4) return toast.error("PIN supervisor minimal 4 digit");
    setCorrecting(true);
    try {
      const r = await api.post(`/reports/reconciliation/${correctionTarget.sale_id}/correct-internal-paid`, {
        supervisor_pin: correctionPin,
        reason: correctionReason,
      });
      toast.success(`Internal PAID dikoreksi ke PENDING.${r.data?.stock_restored ? " Stock dikembalikan." : ""}`);
      setCorrectionTarget(null);
      await load(false);
    } catch(e) { toast.error(safeDetail(e, "Koreksi rekonsiliasi gagal")); }
    finally { setCorrecting(false); }
  };

  const submitRefund = async () => {
    if (!refundTarget) return;
    if (!refundPin || refundPin.length < 4) return toast.error("PIN supervisor minimal 4 digit");
    setRefunding(true);
    try {
      const r = await api.post(`/sales/${refundTarget.sale_id}/refund`, {
        supervisor_pin: refundPin, reason: refundReason,
      });
      toast.success(`Refund disetujui ${r.data?.approver || "Supervisor"}`);
      setRefundTarget(null);
      await load(false);
    } catch(e) { toast.error(safeDetail(e, "Refund gagal")); }
    finally { setRefunding(false); }
  };

  const openLogs = async (row) => {
    if (row.provider !== "yokke") return;
    setLogTarget(row);
    try {
      const r = await api.get("/yokke-logs", { params: { sale_id: row.sale_id } });
      setLogs(r.data || []);
    } catch { setLogs([]); }
  };

  const filteredRows = (data?.rows || []).filter(row => showOnly === "all" || row.recon_state === showOnly);
  const summary = data?.summary || {};
  const outletMap = Object.fromEntries(outlets.map(o => [o.id, o.name]));

  const exportCSV = () => {
    if (!data?.rows?.length) return toast.error("Tidak ada data");
    const headers = ["invoice_no","created_at","outlet","provider","internal_status","provider_status","recon_state","pending_aging","internal_amount","provider_amount","reference"];
    const lines = [headers.join(",")].concat(data.rows.map(row => [
      row.invoice_no,row.created_at,outletMap[row.outlet_id]||"",row.provider,row.internal_status,
      row.provider_status,row.recon_state,row.pending_aging_label||"",row.internal_amount,
      row.provider_amount,row.provider_reference||""
    ].map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")));
    const blob = new Blob([lines.join("\n")], {type:"text/csv;charset=utf-8"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `rekonsiliasi_qris_${start}_${end}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 lg:p-10" data-testid="reconciliation-v3-1">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-6">
        <div>
          <div className="overline text-zinc-500">RECONCILIATION</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Rekonsiliasi QRIS</h1>
          <p className="text-sm text-zinc-500 mt-1">QRIS Dinamis berlaku <strong>5 menit</strong>. PENDING + NOT FOUND = UNKNOWN sebelum expired.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCSV} className="px-4 py-2.5 border border-border text-sm inline-flex gap-2 items-center"><Download className="h-4 w-4"/>Export CSV</button>
          <button onClick={()=>load(true)} disabled={refreshing} className="px-4 py-2.5 bg-[#002FA7] text-white text-sm font-semibold inline-flex gap-2 items-center disabled:opacity-50"><RefreshCw className={"h-4 w-4 " + (refreshing?"animate-spin":"")}/>{refreshing?"Refreshing…":"Refresh Provider"}</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <select value={outletId} onChange={e=>setOutletId(e.target.value)} className="border border-border px-3 py-2 text-sm"><option value="">Semua Outlet</option>{outlets.map(o=><option key={o.id} value={o.id}>{o.name}</option>)}</select>
        <select value={provider} onChange={e=>setProvider(e.target.value)} className="border border-border px-3 py-2 text-sm"><option value="">Semua Provider</option><option value="yokke">Yokke</option><option value="midtrans">Midtrans</option></select>
        <input type="date" value={start} onChange={e=>setStart(e.target.value)} className="border border-border px-3 py-2 text-sm"/>
        <input type="date" value={end} onChange={e=>setEnd(e.target.value)} className="border border-border px-3 py-2 text-sm"/>
        <button onClick={()=>load(false)} className="px-4 py-2 border border-zinc-900 text-sm">Terapkan</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 grid-borders mb-6">
        {[
          ["Total",summary.total||0,""],
          ["Match",summary.matched||0,"text-emerald-700"],
          ["Need Action",summary.need_action||0,"text-amber-700"],
          ["Unknown",summary.unknown||0,"text-zinc-600"],
          ["Mismatch",summary.mismatched||0,"text-rose-700"],
          ["Pending Expired",summary.pending_expired||0,"text-amber-700"],
        ].map(([label,value,cls])=><div key={label} className="p-5 bg-white"><div className="overline text-zinc-500">{label}</div><div className={"font-display font-black text-3xl mt-2 "+cls}>{value}</div></div>)}
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {[["all","Semua"],["match","Match"],["need_action","Need Action"],["unknown","Unknown"],["mismatch","Mismatch"]].map(([key,label])=><button key={key} onClick={()=>setShowOnly(key)} className={"px-4 py-2 text-xs font-semibold uppercase tracking-wider border border-border "+(showOnly===key?"bg-zinc-900 text-white":"bg-white")}>{label}</button>)}
      </div>

      <div className="border border-border overflow-x-auto">
        <table className="w-full min-w-[1500px] text-sm">
          <thead className="bg-zinc-50 border-b border-border"><tr>
            <th className="text-left p-3 overline">Waktu</th><th className="text-left p-3 overline">Invoice</th><th className="text-left p-3 overline">Provider</th><th className="text-left p-3 overline">Internal</th><th className="text-left p-3 overline">Provider Status</th><th className="text-left p-3 overline">Pending Aging</th><th className="text-left p-3 overline">State</th><th className="text-left p-3 overline">Keterangan</th><th className="text-right p-3 overline">Nominal</th><th className="text-right p-3 overline">Aksi</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="p-8 text-center text-zinc-500">Memuat…</td></tr>}
            {!loading && filteredRows.length===0 && <tr><td colSpan={10} className="p-8 text-center text-zinc-500">Tidak ada transaksi</td></tr>}
            {filteredRows.map(row => {
              const meta = STATE_META[row.recon_state] || STATE_META.unknown;
              const StateIcon = meta.icon;
              return <tr key={row.sale_id} className="border-b border-border">
                <td className="p-3 text-xs text-zinc-500 whitespace-nowrap">{formatDate(row.created_at)}</td>
                <td className="p-3 font-mono text-xs">{row.invoice_no}</td>
                <td className="p-3 font-semibold">{row.provider_label}</td>
                <td className="p-3 uppercase text-xs font-semibold">{row.internal_status}</td>
                <td className="p-3 uppercase text-xs">{row.provider_status}</td>
                <td className="p-3">{row.internal_status==="pending"?<span className={"inline-flex items-center gap-1 text-xs font-mono "+(row.pending_expired?"text-rose-700 font-bold":"text-amber-700")}><Clock3 className="h-3.5 w-3.5"/>{row.pending_aging_label||"-"}</span>:<span className="text-zinc-400">-</span>}</td>
                <td className="p-3"><span className={"px-2 py-1 text-[10px] font-bold inline-flex items-center gap-1 "+meta.cls}><StateIcon className="h-3 w-3"/>{meta.label}</span></td>
                <td className="p-3 text-xs max-w-[260px]">{row.state_label}</td>
                <td className="p-3 text-right tabular-nums">{formatIDR(row.internal_amount)}</td>
                <td className="p-3 text-right"><div className="inline-flex items-center justify-end gap-3 whitespace-nowrap">
                  {row.can_refresh && <button onClick={()=>inquiryRow(row)} disabled={busyRow===row.sale_id} className="text-xs text-[#002FA7] underline inline-flex gap-1 items-center"><RefreshCw className={"h-3 w-3 "+(busyRow===row.sale_id?"animate-spin":"")}/>Inquiry</button>}
                  {row.can_finalize_paid && <button onClick={()=>finalizePaid(row)} disabled={busyRow===row.sale_id} className="text-xs text-emerald-700 font-semibold underline inline-flex gap-1 items-center"><CheckCheck className="h-3 w-3"/>Finalisasi PAID</button>}
                  {row.can_close_expired && <button onClick={()=>{setExpireTarget(row);setExpirePin("");setExpireReason("QRIS melewati masa berlaku 5 menit");}} className="text-xs text-rose-700 font-semibold underline">Tutup EXPIRED</button>}
                  {row.can_recover_expired_paid && <button onClick={()=>{setRecoveryTarget(row);setRecoveryPin("");setRecoveryReason("Late payment recovery: provider sukses setelah Internal EXPIRED");}} className="text-xs text-emerald-700 font-semibold underline inline-flex gap-1 items-center"><CheckCheck className="h-3 w-3"/>Pulihkan PAID</button>}
                  {row.can_correct_internal_paid && <button onClick={()=>{setCorrectionTarget(row);setCorrectionPin("");}} className="text-xs text-amber-700 font-semibold underline inline-flex gap-1 items-center"><Wrench className="h-3 w-3"/>Koreksi Internal</button>}
                  {canRefund && row.can_refund && <button onClick={()=>{setRefundTarget(row);setRefundPin("");}} className="text-xs text-rose-700 underline inline-flex gap-1 items-center"><RotateCcw className="h-3 w-3"/>Refund</button>}
                  {row.provider==="yokke" && <button onClick={()=>openLogs(row)} className="text-xs text-zinc-500 underline inline-flex gap-1 items-center"><Eye className="h-3 w-3"/>Logs</button>}
                </div></td>
              </tr>
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!expireTarget} onOpenChange={open=>!open && !expiring && setExpireTarget(null)}>
        <DialogContent className="rounded-none border-border max-w-md"><DialogHeader><DialogTitle>Tutup Transaksi sebagai EXPIRED</DialogTitle></DialogHeader>
          {expireTarget && <div className="space-y-4">
            <div className="border border-rose-200 bg-rose-50 p-4 text-sm"><div className="font-mono text-xs">{expireTarget.invoice_no}</div><div className="font-black text-xl mt-1">{expireTarget.provider_label} · {expireTarget.provider_status}</div><div className="mt-2 text-xs">Aging: <strong>{expireTarget.pending_aging_label}</strong></div></div>
            <div className="text-xs bg-amber-50 border border-amber-200 p-3">Backend melakukan <strong>live inquiry lagi</strong>. Jika provider sukses atau inquiry error/timeout, transaksi tidak akan ditutup.</div>
            <div><label className="overline block mb-1">PIN Supervisor/Admin</label><div className="relative"><KeyRound className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/><input type="password" value={expirePin} onChange={e=>setExpirePin(e.target.value)} maxLength={8} className="w-full border border-border pl-9 pr-3 py-3"/></div></div>
            <div><label className="overline block mb-1">Alasan</label><input value={expireReason} onChange={e=>setExpireReason(e.target.value)} className="w-full border border-border px-3 py-2"/></div>
            <div className="grid grid-cols-2 gap-2"><button onClick={()=>setExpireTarget(null)} disabled={expiring} className="py-3 border border-border">Batal</button><button onClick={submitExpire} disabled={expiring} className="py-3 bg-rose-600 text-white font-semibold">{expiring?"Verifikasi…":"Approve & Close"}</button></div>
          </div>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!recoveryTarget} onOpenChange={open=>!open && !recovering && setRecoveryTarget(null)}>
        <DialogContent className="rounded-none border-border max-w-md">
          <DialogHeader><DialogTitle>Pulihkan Late Payment menjadi PAID</DialogTitle></DialogHeader>
          {recoveryTarget && <div className="space-y-4">
            <div className="border border-emerald-200 bg-emerald-50 p-3 text-xs">
              Internal sudah <strong>EXPIRED</strong>, tetapi provider menunjukkan <strong>SUCCESS</strong>.
              Backend akan inquiry ulang. Jika tetap sukses, transaksi dikembalikan ke flow finalisasi normal:
              stock, PAID, settlement, dan audit. Jika finalisasi gagal, transaksi dipertahankan PENDING dengan flag recovery_required.
            </div>
            <input type="password" placeholder="PIN Supervisor/Admin" value={recoveryPin} onChange={e=>setRecoveryPin(e.target.value)} maxLength={8} className="w-full border border-border px-3 py-3"/>
            <input value={recoveryReason} onChange={e=>setRecoveryReason(e.target.value)} className="w-full border border-border px-3 py-2"/>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={()=>setRecoveryTarget(null)} disabled={recovering} className="py-3 border border-border">Batal</button>
              <button onClick={submitRecovery} disabled={recovering} className="py-3 bg-emerald-600 text-white font-semibold">{recovering?"Memulihkan…":"Approve & Pulihkan"}</button>
            </div>
          </div>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!correctionTarget} onOpenChange={open=>!open && !correcting && setCorrectionTarget(null)}>
        <DialogContent className="rounded-none border-border max-w-md"><DialogHeader><DialogTitle>Koreksi Internal PAID</DialogTitle></DialogHeader>
          {correctionTarget && <div className="space-y-4"><div className="text-xs border border-amber-200 bg-amber-50 p-3">Yokke akan di-inquiry ulang. Jika tetap belum sukses, PAID dikoreksi ke PENDING dan stock yang pernah terpotong dikembalikan.</div><input type="password" placeholder="PIN Supervisor/Admin" value={correctionPin} onChange={e=>setCorrectionPin(e.target.value)} className="w-full border border-border px-3 py-3"/><input value={correctionReason} onChange={e=>setCorrectionReason(e.target.value)} className="w-full border border-border px-3 py-2"/><button onClick={submitCorrection} disabled={correcting} className="w-full py-3 bg-amber-600 text-white font-semibold">{correcting?"Memproses…":"Approve & Koreksi"}</button></div>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!refundTarget} onOpenChange={open=>!open && !refunding && setRefundTarget(null)}>
        <DialogContent className="rounded-none border-border max-w-md"><DialogHeader><DialogTitle>Refund QRIS</DialogTitle></DialogHeader>
          {refundTarget && <div className="space-y-4"><div className="text-xs border border-amber-200 bg-amber-50 p-3">Refund hanya pada hari WIB yang sama dan usia pembayaran kurang dari 60 menit.</div><input type="password" placeholder="PIN Supervisor/Admin" value={refundPin} onChange={e=>setRefundPin(e.target.value)} className="w-full border border-border px-3 py-3"/><input value={refundReason} onChange={e=>setRefundReason(e.target.value)} className="w-full border border-border px-3 py-2"/><button onClick={submitRefund} disabled={refunding} className="w-full py-3 bg-rose-600 text-white font-semibold">{refunding?"Memproses…":"Approve & Refund"}</button></div>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!logTarget} onOpenChange={open=>!open && setLogTarget(null)}>
        <DialogContent className="rounded-none border-border max-w-3xl max-h-[80vh] overflow-y-auto"><DialogHeader><DialogTitle>Yokke API Logs · {logTarget?.invoice_no || ""}</DialogTitle></DialogHeader><div className="space-y-3">{logs.length===0 && <div className="text-sm text-zinc-500">Tidak ada log.</div>}{logs.map(log=><div key={log.id} className="border border-border p-3"><div className="flex justify-between text-xs"><strong>{log.operation}</strong><span>{formatDate(log.created_at)}</span></div><pre className="text-[10px] mt-2 whitespace-pre-wrap break-all">{JSON.stringify(log.response || log.error || {}, null, 2)}</pre></div>)}</div></DialogContent>
      </Dialog>
    </div>
  );
}
